import { Injectable } from '@angular/core';
import { UserSkillModel, VacancyPreferencesModel } from '@digilize/shared/definitions/src';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProfileChangesService {
  private readonly _pendingVacancyPreferences = new BehaviorSubject<VacancyPreferencesModel | null>(null);
  private readonly _hasChanges = new BehaviorSubject<boolean>(false);
  private readonly _pendingRatingChanges = new Map<string, UserSkillModel>();
  private readonly _reorderedSections = new Set<string>();
  private readonly _pendingSkillDeletions = new Set<string>();
  private _pendingVisibility: boolean | null = null;
  private _pendingPhotoUpdate = false;

  readonly saveRequested$ = new Subject<void>();
  readonly saveCompleted$ = new Subject<void>();
  readonly pendingVacancyPreferences$ = this._pendingVacancyPreferences.asObservable();
  readonly hasChanges$ = this._hasChanges.asObservable();

  get pendingVacancyPreferences(): VacancyPreferencesModel | null {
    return this._pendingVacancyPreferences.value;
  }

  get hasChanges(): boolean {
    return this._hasChanges.value;
  }

  get pendingRatingChanges(): UserSkillModel[] {
    return Array.from(this._pendingRatingChanges.values());
  }

  get hasPendingRatingChanges(): boolean {
    return this._pendingRatingChanges.size > 0;
  }

  get reorderedSections(): string[] {
    return Array.from(this._reorderedSections);
  }

  get pendingVisibility(): boolean | null {
    return this._pendingVisibility;
  }

  get pendingSkillDeletions(): string[] {
    return Array.from(this._pendingSkillDeletions);
  }

  get pendingPhotoUpdate(): boolean {
    return this._pendingPhotoUpdate;
  }

  markPhotoChanged(): void {
    this._pendingPhotoUpdate = true;
    this._hasChanges.next(true);
  }

  addPendingSkillDeletion(id: string): void {
    this._pendingSkillDeletions.add(id);
    this._hasChanges.next(true);
  }

  addRatingChange(skill: UserSkillModel): void {
    this._pendingRatingChanges.set(skill.id, skill);
    this._hasChanges.next(true);
  }

  markSectionReordered(section: string): void {
    this._reorderedSections.add(section);
    this._hasChanges.next(true);
  }

  setVacancyPreferences(preferences: VacancyPreferencesModel): void {
    this._pendingVacancyPreferences.next(preferences);
    this._hasChanges.next(true);
  }

  setVisibility(visible: boolean): void {
    this._pendingVisibility = visible;
    this._hasChanges.next(true);
  }

  markAsSaved(): void {
    this._pendingVacancyPreferences.next(null);
    this._pendingRatingChanges.clear();
    this._reorderedSections.clear();
    this._pendingSkillDeletions.clear();
    this._pendingVisibility = null;
    this._pendingPhotoUpdate = false;
    this._hasChanges.next(false);
  }

  markFormDirty(): void {
    this._hasChanges.next(true);
  }
}
